import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  AvatarGroup,
  Tooltip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Group as TeamIcon,
  Schedule as ScheduleIcon,
  TrendingUp as ProgressIcon,
  AttachMoney as BudgetIcon,
} from '@mui/icons-material';
import { ProjectManagerDashboardService, PMProject } from '../../services/ProjectManagerDashboardService';

/**
 * Active Projects Component for Project Manager
 * Displays all active projects with management capabilities
 */
const ActiveProjects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<PMProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<PMProject | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Get all projects and filter for active ones
      const allProjects = await ProjectManagerDashboardService.getProjects();
      const activeProjects = allProjects.filter(project => 
        project.status.toLowerCase() === 'in progress' || 
        project.status.toLowerCase() === 'active' ||
        project.status.toLowerCase() === 'in_progress'
      );
      
      setProjects(activeProjects);
    } catch (err) {
      setError('Failed to load active projects. Please try again.');
      console.error('Error loading active projects:', err);
      
      // Fallback to mock data for development
      setProjects([
        {
          id: 1,
          name: "E-commerce Platform Redesign",
          client: "TechCorp Solutions",
          developer: "Sarah Johnson",
          status: "In Progress",
          progress: 75,
          budget: "$25,000",
          priority: "high",
          startDate: "2024-01-15",
          endDate: "2024-03-30",
          description: "Complete redesign of the e-commerce platform with modern UI/UX",
          created_at: "2024-01-15T10:00:00Z",
          updated_at: "2024-02-20T14:30:00Z"
        },
        {
          id: 2,
          name: "Mobile App Development",
          client: "StartupXYZ",
          developer: "Mike Chen",
          status: "In Progress",
          progress: 45,
          budget: "$18,000",
          priority: "medium",
          startDate: "2024-02-01",
          endDate: "2024-05-15",
          description: "Native mobile app for iOS and Android",
          created_at: "2024-02-01T09:00:00Z",
          updated_at: "2024-02-28T16:45:00Z"
        },
        {
          id: 3,
          name: "Dashboard Analytics Implementation",
          client: "DataFlow Inc",
          developer: "Alex Rodriguez",
          status: "In Progress",
          progress: 90,
          budget: "$12,500",
          priority: "high",
          startDate: "2024-01-20",
          endDate: "2024-03-15",
          description: "Advanced analytics dashboard with real-time data visualization",
          created_at: "2024-01-20T11:30:00Z",
          updated_at: "2024-03-10T13:20:00Z"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, project: PMProject) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const handleViewProject = () => {
    if (selectedProject) {
      // Navigate to project detail view using React Router
      navigate(`/dashboard/pm/projects/${selectedProject.id}`);
    }
    handleMenuClose();
  };

  const handleEditProject = () => {
    if (selectedProject) {
      // Open edit project dialog or navigate to edit page
      alert(`Edit project: ${selectedProject.name}`);
    }
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in progress': return 'primary';
      case 'active': return 'success';
      case 'review': return 'warning';
      case 'completed': return 'success';
      case 'on hold': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Active Projects
        </Typography>
        <Button variant="contained" color="primary">
          Create New Project
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={loadProjects}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      {projects.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Active Projects
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You don't have any active projects assigned yet.
              </Typography>
              <Button variant="contained" sx={{ mt: 2 }}>
                Create Your First Project
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 3 }}>
          {projects.map((project) => (
            <Card key={project.id} variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" sx={{ mb: 2 }}>
                  <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
                    {project.name}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {project.priority && (
                      <Chip 
                        label={project.priority.toUpperCase()} 
                        size="small" 
                        color={getPriorityColor(project.priority)}
                        variant="outlined"
                      />
                    )}
                    <Chip 
                      label={project.status} 
                      size="small" 
                      color={getStatusColor(project.status)}
                    />
                    <IconButton 
                      size="small"
                      onClick={(e) => handleMenuOpen(e, project)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {project.description || 'No description available'}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {project.progress}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={project.progress} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} sx={{ mb: 2 }}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      <TeamIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Client
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="medium">
                      {project.client}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      <BudgetIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Budget
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="medium">
                      {project.budget || 'Not set'}
                    </Typography>
                  </Box>
                </Box>

                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Start Date
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(project.startDate)}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Due Date
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(project.endDate)}
                    </Typography>
                  </Box>
                </Box>

                {project.developer && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                        {project.developer.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      <Typography variant="body2" color="text.secondary">
                        Assigned to: <strong>{project.developer}</strong>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Project Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleViewProject}>
          <ViewIcon sx={{ mr: 1 }} fontSize="small" />
          View Project
        </MenuItem>
        <MenuItem onClick={handleEditProject}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Project
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ActiveProjects;